import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiLink, FiCheckCircle, FiAlertTriangle, FiLock } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../constants/constants';
import Button from '../components/common/Button';

/**
 * Magic-link sign-in interstitial. Token is consumed via POST after the
 * user clicks "Confirm sign-in" — not auto-consumed on mount — so email
 * scanners (Safe Links / Mimecast / Proofpoint) pre-fetching the URL
 * don't burn the single-use token.
 */
export default function MagicLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Must use AuthContext.magicLogin (not authAPI directly) so React's
  // user state stays in sync with localStorage tokens — otherwise
  // ProtectedRoute bounces us back to /login after navigate.
  const { magicLogin } = useAuth();

  // Capture token once on mount; the URL gets stripped below.
  const [token] = useState(() => searchParams.get('token'));
  const [phase, setPhase] = useState('awaiting-confirm'); // 'awaiting-confirm' | 'verifying' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Strip the token from the visible URL — keeps it out of browser history,
  // Referer headers, and access logs.
  useEffect(() => {
    if (token && window.history.replaceState) {
      window.history.replaceState(null, '', '/magic-login');
    }
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setErrorMessage('');
    setPhase('verifying');
    try {
      const response = await magicLogin(token, /* rememberMe */ false);
      if (response?.success && response?.data?.tokens) {
        setPhase('success');
        // Short delay so the success state is perceivable before we navigate
        setTimeout(() => navigate(ROUTES.DASHBOARD, { replace: true }), 800);
      } else {
        throw new Error(response?.message || 'Invalid or expired sign-in link.');
      }
    } catch (err) {
      setErrorMessage(err?.message || 'Invalid or expired sign-in link.');
      setPhase('error');
    }
  };

  /* ── Shared layout shell (mirrors ResetPasswordPage) ── */
  const Shell = ({ children }) => (
    <div className="h-[var(--app-vh)] w-full flex overflow-hidden" style={{ background: '#f4f0e8' }}>
      {/* Left decorative panel */}
      <div className="relative hidden lg:flex h-[var(--app-vh)] flex-1 px-[52px] py-12 flex-col justify-center items-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0a1628 0%,#0d2a3a 40%,#0a3d2a 100%)' }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif" }}
          className="text-[32px] font-bold text-[#b8973a] text-center">
          ComplyV
        </div>
        <div className="text-[13px] text-white/50 mt-2 text-center">
          Corporate Governance & Compliance Platform
        </div>
      </div>

      {/* Right action panel */}
      <div className="w-full lg:w-[460px] h-[var(--app-vh)] bg-[#f4f0e8] overflow-y-auto px-7 md:px-12 py-10 md:py-[52px] flex items-center">
        <div className="w-full max-w-[364px] mx-auto text-center">
          {children}
        </div>
      </div>
    </div>
  );

  /* ── No token in URL ── */
  if (!token) {
    return (
      <Shell>
        <div className="flex justify-center mb-3"><FiLink size={40} style={{ color: '#145f5f' }} /></div>
        <div className="text-[20px] font-bold text-[#08111f] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Invalid Sign-in Link
        </div>
        <div className="text-[13px] text-[#7a7060] mb-6 leading-relaxed">
          This sign-in link is missing or malformed. Request a new one from the login page.
        </div>
        <Button block size="lg" onClick={() => navigate(ROUTES.LOGIN)}>
          Go to Login
        </Button>
      </Shell>
    );
  }

  /* ── Success ── */
  if (phase === 'success') {
    return (
      <Shell>
        <div className="flex justify-center mb-3"><FiCheckCircle size={40} style={{ color: '#4a6b3f' }} /></div>
        <div className="text-[22px] font-bold text-[#08111f] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Signed in!
        </div>
        <div className="text-[12.5px] text-[#7a7060]">
          Taking you to your dashboard…
        </div>
      </Shell>
    );
  }

  /* ── Error after consume attempt ── */
  if (phase === 'error') {
    return (
      <Shell>
        <div className="flex justify-center mb-3"><FiAlertTriangle size={40} style={{ color: '#9a564a' }} /></div>
        <div className="text-[20px] font-bold text-[#08111f] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Link expired or already used
        </div>
        <div className="mb-4 text-[12.5px] px-3 py-2 rounded-lg border bg-[#f2e9e6] border-[#e6d2cb] text-[#9a564a]">
          {errorMessage}
        </div>
        <div className="text-[13px] text-[#7a7060] mb-6 leading-relaxed">
          Magic-link sign-in URLs are single-use and expire after 15 minutes.
          Request a fresh one from the login page.
        </div>
        <Button block size="lg" onClick={() => navigate(ROUTES.LOGIN)}>
          Back to Login
        </Button>
      </Shell>
    );
  }

  /* ── Awaiting confirm (default) ── */
  return (
    <Shell>
      <div className="flex justify-center mb-3"><FiLock size={40} style={{ color: '#145f5f' }} /></div>
      <div className="text-[24px] font-bold text-[#08111f] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Confirm sign-in
      </div>
      <div className="text-[13px] text-[#7a7060] mb-6 leading-relaxed">
        You're about to sign in to your ComplyV account using the
        link from your email. Click below to continue.
      </div>

      <Button
        block
        size="lg"
        loading={phase === 'verifying'}
        onClick={handleConfirm}
      >
        {phase === 'verifying' ? 'Signing you in…' : 'Confirm sign-in →'}
      </Button>

      <div className="text-[11px] text-[#9a9080] mt-5 leading-relaxed">
        Didn't request this? You can safely close this tab — no action will be
        taken on your account.
      </div>
    </Shell>
  );
}
